import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@monksflow/monks-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { workspaceUsersQuery } from '@/entities/project-member';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { TagInput } from '@/shared/ui';

import type { ApprovalChainAssigneeOption, ApprovalChainFormInput } from '../../model/schema';
import type { ApproverOption, StepRoleCatalogue } from './ApproverPicker.types';
import { ApproverChip, ApproverOptionRow } from './ApproverPickerItems';
import {
  approverOptionLabel,
  approverOptionValue,
  dedupeByUserId,
  roleHasNoHolders,
  roleOptions,
  selectedApprovers,
  splitSelection,
} from './approver-options.utils';

/**
 * Minimum characters before the picker searches workspace users server-side.
 * Matches the Platform API's own floor (`workspaceUsersQuery` rejects 1–2 char
 * terms) and keeps the initial-open experience project-members-only.
 */
const MIN_WORKSPACE_SEARCH_LENGTH = 3;

export interface ApproverPickerProps {
  /** Zero-based position of the step whose approvers this edits. */
  index: number;
  /** Project members offered before any search. */
  members: ApprovalChainAssigneeOption[];
  isLoadingMembers: boolean;
  /** Workspace id used to search workspace-level users server-side. */
  workspaceId: string | null | undefined;
  stepRoles: StepRoleCatalogue;
  disabled: boolean;
}

/** One field for who may approve a step: at most one project role, and any number of people. */
export function ApproverPicker(props: ApproverPickerProps) {
  const { t } = useTranslation('approvalChains');
  const { index, members, stepRoles } = props;
  const { control, setValue, watch } = useFormContext<ApprovalChainFormInput>();
  const approverRole = watch(`steps.${index}.approverRole`) ?? null;

  const [searchInput, setSearchInput] = useState('');
  const workspaceOptions = useWorkspaceSearch(props.workspaceId, searchInput, members);
  const offeredRoles = roleOptions(stepRoles);

  return (
    <FormField
      control={control}
      name={`steps.${index}.assignees`}
      render={({ field }) => {
        const people = dedupeByUserId([...members, ...workspaceOptions, ...field.value]);
        const groups = [
          ...(offeredRoles
            ? [{ label: t('manageApprovalChains.approverPicker.roles'), items: offeredRoles }]
            : []),
          {
            label: t('manageApprovalChains.approverPicker.people'),
            items: people.map((person): ApproverOption => ({ kind: 'person', person })),
          },
        ];

        const handleChange = (selection: ApproverOption[]) => {
          const next = splitSelection(selection);
          setValue(`steps.${index}.approverRole`, next.role, {
            shouldDirty: true,
            shouldValidate: true,
          });
          field.onChange(next.people);
        };

        return (
          <FormItem>
            <FormLabel>
              {t('manageApprovalChains.approvalChainStepRow.approvers')}
              <span aria-hidden="true" className="text-destructive">
                {' '}
                *
              </span>
            </FormLabel>
            <FormControl>
              <TagInput<ApproverOption>
                value={selectedApprovers(approverRole, field.value)}
                onChange={handleChange}
                options={[]}
                groups={groups}
                getOptionLabel={approverOptionLabel}
                getOptionValue={approverOptionValue}
                disabled={props.disabled || props.isLoadingMembers}
                inputValue={searchInput}
                onInputValueChange={setSearchInput}
              >
                <TagInput.Chips<ApproverOption>
                  placeholder={t('manageApprovalChains.approvalChainStepRow.addApprovers')}
                  ariaLabel={t('manageApprovalChains.approvalChainStepRow.approversForStep', {
                    step: index + 1,
                  })}
                  className="min-h-11"
                  renderChip={(option, onRemove) => (
                    <ApproverChip option={option} onRemove={onRemove} />
                  )}
                />
                <TagInput.Content<ApproverOption>
                  renderOption={(option) => <ApproverOptionRow option={option} />}
                />
              </TagInput>
            </FormControl>
            {approverRole && roleHasNoHolders(stepRoles, approverRole) && (
              <p className="text-sm text-warning-foreground" data-testid="approver-role-no-holders">
                {t('manageApprovalChains.approverPicker.nobodyHoldsRoleYet', {
                  role: approverRole.name,
                })}
              </p>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

/** Workspace users matching the search, minus the project members already offered. */
function useWorkspaceSearch(
  workspaceId: string | null | undefined,
  searchInput: string,
  members: ApprovalChainAssigneeOption[],
): ApprovalChainAssigneeOption[] {
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const { data } = useQuery({
    ...workspaceUsersQuery(workspaceId, debouncedSearch),
    enabled: !!workspaceId && debouncedSearch.trim().length >= MIN_WORKSPACE_SEARCH_LENGTH,
  });

  return useMemo(() => {
    const memberIds = new Set(members.map((member) => member.userId));
    return (data?.data ?? [])
      .filter((user) => !memberIds.has(user.id))
      .map((user) => ({
        userId: user.id,
        name: user.name,
        email: user.email ?? '',
        avatarUrl: user.avatarUrl ?? null,
      }));
  }, [members, data]);
}
